import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { getHttpClient, useHttpClient } from '@bonusly/http-client';
import { Recipient, RecipientAPIData } from '@bonusly/recipient-entities';
import { useDispatch } from '@bonusly/events';

import { GIVE_RECOGNITION_MODAL_QUERY_PARAM, GIVE_RECOGNITION_TEXT_QUERY_PARAM } from '../constants';

// Module-level variable to ensure hook only runs once across component remounts
let hasInitializedGlobally = false;

/**
 * URL parameter value types for give recognition modal
 * - null: parameter not present
 * - 'true': open modal without pre-selection
 * - string: user ID or group token to pre-select
 */
type GiveRecognitionParam = string | null;

/**
 * Hook that checks for URL parameters to automatically open the Give Recognition Modal
 * with optional user pre-selection and text. Uses the existing 'init-give-recognition' event
 * system that's already used throughout the codebase.
 *
 * Examples:
 * - ?give_recognition=true - Opens modal without pre-selection
 * - ?give_recognition=68a5045a50720374d58d0de3 - Opens modal with user pre-selected by userId
 * - ?give_recognition=userId&give_recognition_text=Great job! - Opens modal with both user and text
 */
export const useInitGiveRecognitionModal = () => {
	const [params] = useSearchParams();
	const dispatch = useDispatch();
	const http = useHttpClient('v2');

	// Using useEffect to handle URL-based modal initialization
	useEffect(() => {
		// Skip if already initialized or no relevant query params
		if (hasInitializedGlobally ||
			(!params.has(GIVE_RECOGNITION_MODAL_QUERY_PARAM) && !params.has(GIVE_RECOGNITION_TEXT_QUERY_PARAM))) {
			return;
		}

		const initModal = async () => {
			const userParam: GiveRecognitionParam = params.get(GIVE_RECOGNITION_MODAL_QUERY_PARAM);
			const textParam = params.get(GIVE_RECOGNITION_TEXT_QUERY_PARAM);
			hasInitializedGlobally = true;

			// If no user param
			if (!userParam) {
				return;
			}

			try {
				const userData = await http.get(`/user_management/users/${userParam}`);

				if (userData.data) {
					const recipientData = {
						id: userData.data.id,
						display_name: userData.data.display_name,
						username: userData.data.username,
						profile_pic_url: userData.data.profile_pic_url,
						user_mode: 'user'
					};

					const recipientEntity = new Recipient(recipientData);

					dispatch('init-give-recognition', {
						recipient: recipientEntity,
						text: textParam || undefined
					});
				} else {
					dispatch('init-give-recognition', textParam ? { text: textParam } : undefined);
				}
			} catch (directLookupError) {
				try {
					const httpV1 = await getHttpClient('v1');
					const data = await httpV1.get('/users/autocomplete', {
						params: { search: userParam }
					});

					const recipients = data.data.result as RecipientAPIData[];
					const recipient = recipients.find((r) => r.id === userParam);

					if (recipient) {
						const recipientEntity = new Recipient(recipient);

						dispatch('init-give-recognition', {
							recipient: recipientEntity,
							text: textParam || undefined
						});
					} else {
						dispatch('init-give-recognition', textParam ? { text: textParam } : undefined);
					}
				} catch (autocompleteError) {
					console.error('Error fetching user for give recognition modal:', autocompleteError);
					dispatch('init-give-recognition', textParam ? { text: textParam } : undefined);
				}
			}
		};

		initModal();
	}, [dispatch, http, params]);
};